import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useExperience } from '../../context/ExperienceContext';
import { mv3 } from '../../styles/MV3_Styles';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

export function Modal({ title, onClose, children, size = 'md' }: ModalProps) {
  const { isMangaV3 } = useExperience();
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    // Lock vertical scroll only — leaving touch-action/horizontal alone keeps
    // pinch-to-zoom working on the modal content.
    document.body.style.overflowY = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflowY = 'unset';
    };
  }, [handleEscape]);

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900/60 dark:bg-black/70 backdrop-blur-sm transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div
          className={`inline-block w-full ${sizeClasses[size]} p-0 my-8 overflow-hidden text-left align-middle bg-white dark:bg-gray-800 rounded-2xl shadow-warm-xl transform transition-all animate-slide-up`}
          style={isMangaV3 ? mv3.modal : undefined}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3
              id="modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white font-display"
              style={isMangaV3 ? mv3.heading : undefined}
            >
              {title}
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              style={isMangaV3 ? { borderRadius: 2 } : undefined}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div className="px-6 py-5 max-h-[calc(100vh-200px)] overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
